<template>
  <section>
    <v-container fluid>
      <v-row class="justify-between align-center">
        <v-col cols="auto">
          <v-btn class="accent shadow-4 px-8" large rounded @click="isFormOpen = true">
            Añadir Plantilla
          </v-btn>
        </v-col>

        <v-col cols="auto" class="ml-auto">
          <v-text-field
            filled
            rounded
            dense
            v-model="search"
            append-icon="mdi-magnify"
            label="Buscar"
            single-line
            hide-details
          ></v-text-field>
        </v-col>
      </v-row>
    </v-container>

    <v-card class="shadow-1">
      <v-data-table
        :headers="headers"
        :items="tableList"
        :options.sync="tableOptions"
        :search="search"
        :loading="isLoading || isLoadingGateways || isLoadingConfig"
        show-expand
        single-expand
        item-key="id"
        :footer-props="{
          'items-per-page-options': [15, 30, 50, 100],
        }"
      >
        <!-- Slot para las acciones -->
        <template v-slot:[`item.action`]="{ item }">
          <v-hover v-slot:default="{ hover }">
            <v-btn text icon :color="hover ? 'accent' : 'blue-grey'" @click.stop="handleClickEdit(item)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </v-hover>

          <v-hover v-slot:default="{ hover }">
            <v-btn text icon :color="hover ? 'error' : 'blue-grey'" @click.stop="handleClickDelete(item)">
              <v-icon>mdi-trash-can-outline</v-icon>
            </v-btn>
          </v-hover>
        </template>

        <!-- Slot para el contenido desplegable -->
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="py-4 grey lighten-4">
            <v-container fluid>
              <v-row dense class="text-caption">
                <v-col cols="12" sm="6" md="3">
                  <div class="font-weight-bold grey--text text--dark-2">Configuración:</div>
                  <div>{{ getConfigName(item.config_id) }}</div>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <div class="font-weight-bold grey--text text--dark-2">Gateway:</div>
                  <div>{{ getGatewayName(item.gateway_id) }}</div>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <div class="font-weight-bold grey--text text--dark-2">Número de Parámetro:</div>
                  <div>{{ item.parameter_number || "N/A" }}</div>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <div class="font-weight-bold grey--text text--dark-2">Parámetro:</div>
                  <div>{{ getParameterLabel(item.parameter) }}</div>
                </v-col>
              </v-row>
            </v-container>
          </td>
        </template>
      </v-data-table>
    </v-card>

    <template-dialog :open="isFormOpen" :editData="itemToEdit" @dialog:close="closeForm"></template-dialog>
    <core-delete-dialog
      :open="isDeleteOpen"
      :onDelete="deleteItem"
      title="plantilla"
      @dialog:close="closeDeleteDialog"
    ></core-delete-dialog>
  </section>
</template>

<script>
import TemplateDialog from "@/components/Appointments/TemplateDialog.vue";
import { PbxHttpClient } from "@/services/httpclient";
import CoreDeleteDialog from "@/components/Utils/CoreDeleteDialog.vue";

export default {
  components: { TemplateDialog, CoreDeleteDialog },
  data() {
    return {
      isLoading: false,
      isLoadingGateways: false,
      isLoadingConfig: false,
      isFormOpen: false,
      isDeleteOpen: false,
      search: "",
      gatewayList: [],
      whatsappList: [],
      headers: [
        {
          text: "Título",
          value: "title",
          class: "primary--text",
          width: "20%",
          align: "left",
        },
        {
          text: "Introducción",
          value: "introduction",
          class: "primary--text",
          width: "25%",
        },
        {
          text: "Cliente",
          value: "client_full_name_placeholder",
          class: "primary--text",
          width: "20%",
        },
        {
          text: "Dirección",
          value: "address",
          class: "primary--text",
          width: "15%",
        },
        {
          text: "Acciones",
          value: "action",
          class: "primary--text",
          width: "10%",
          align: "center",
          sortable: false,
        },
        {
          text: "",
          value: "data-table-expand",
          class: "primary--text",
          width: "5%",
        },
      ],
      tableOptions: {
        page: 1,
        itemsPerPage: 15,
      },
      tableList: [],
      totalItems: 0,
      itemToEdit: null,
    };
  },

  mounted() {
    this.getGateways();
    this.getWhatsappList();
  },

  watch: {
    tableOptions: {
      handler() {
        this.getData();
      },
      deep: true,
    },
  },

  methods: {
    async getGateways() {
      this.isLoadingGateways = true;
      try {
        const { data } = await PbxHttpClient.get("/gateway", {
          params: { page: 1, itemPerPage: 100 },
        });
        this.gatewayList = data.data || [];
      } catch (err) {
        console.error("Error al obtener gateways:", err);
      }
      this.isLoadingGateways = false;
    },

    async getWhatsappList() {
      this.isLoadingConfig = true;
      try {
        const { data } = await PbxHttpClient.get("/whatsapps/config/index_select");
        this.whatsappList = data || [];
      } catch (err) {
        console.error("Error al obtener lista whatsapp:", err);
      }
      this.isLoadingConfig = false;
    },

    // Métodos para mapear IDs a Nombre / Texto representativo
    getConfigName(id) {
      if (!id) return "N/A";
      const config = this.whatsappList.find((item) => item.id === id);
      return config ? config.name : id;
    },

    getGatewayName(id) {
      if (!id) return "N/A";
      const gateway = this.gatewayList.find((item) => item.id === id);
      return gateway ? gateway.name : id;
    },

    getParameterLabel(param) {
      if (!param) return "N/A";

      // Mapeo opcional si deseas mostrar una etiqueta más amigable
      const labels = {
        name: "Nombre",
        specialty: "Especialidad",
        date: "Fecha",
        time: "Hora",
        responsible: "Responsable",
        direction: "Dirección",
        others: "Otros",
      };

      return labels[param] || param;
    },

    async getData() {
      this.isLoading = true;
      try {
        const { sortDesc, page, itemsPerPage } = this.tableOptions;

        const { data } = await PbxHttpClient.get("/module_appointment/templates_appointments", {
          params: { page, itemPerPage: itemsPerPage, prioritySort: sortDesc },
        });
        this.tableList = data.data;
        this.totalItems = data.total;
      } catch (error) {
        console.log(error);
      }
      this.isLoading = false;
    },
    closeForm(item = false) {
      this.isFormOpen = false;
      if (item) {
        if (this.itemToEdit) {
          this.tableList = this.tableList.map((el) => (el.id === item.id ? item : el));
        } else this.tableList = [...this.tableList, item];
      }
      this.itemToEdit = null;
    },
    closeDeleteDialog() {
      this.itemToEdit = null;
      this.isDeleteOpen = false;
    },
    handleClickEdit(item) {
      this.itemToEdit = item;
      this.isFormOpen = true;
    },
    handleClickDelete(item) {
      this.itemToEdit = item;
      this.isDeleteOpen = true;
    },

    async deleteItem() {
      try {
        await PbxHttpClient.delete(`/module_appointment/templates_appointments/${this.itemToEdit.id}`);
        this.$snotify.success("Plantilla eliminada con exito", "¡Hecho!");
        this.tableList = this.tableList.filter((el) => el.id !== this.itemToEdit.id);
        return true;
      } catch (error) {
        this.$snotify.error("Hubo un error eliminando la plantilla", "¡Error!");
        console.log("error deleting template", error);
        return false;
      }
    },
  },
};
</script>
